import * as React from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox, { CheckboxProps } from '@mui/material/Checkbox';

/**
 * One of the three possible checkbox states.
 */
interface MCheckboxStatus {
    /** Numeric identifier used to cycle between states. */
    id: number;
    /** `true`/`false` for checked/unchecked, `null` for indeterminate. */
    checked: boolean | null;
    /** Whether the MUI `Checkbox` renders as indeterminate. */
    indeterminate: boolean;
}

/**
 * Props for {@link MCheckbox}.
 */
interface MCheckboxProps extends Omit<CheckboxProps, 'checked' | 'indeterminate' | 'onChange' | 'defaultValue' | 'name'> {
    /** Name applied to the underlying `Checkbox` input. */
    source?: string;
    /** Label rendered next to the checkbox. */
    label?: React.ReactNode;
    /**
     * Called with the new checked state.
     *
     * @param checked - `true`/`false`, or `null` when {@link nullable} and the
     * indeterminate state is selected.
     */
    onChange: (checked: boolean | null) => void;
    /** Initial checked state. `null` implies a tri-state (indeterminate-capable) checkbox. Defaults to `false`. */
    defaultValue?: boolean | null;
    /** Allows cycling through an indeterminate ("null") state. Defaults to `false`. */
    nullable?: boolean;
}

/**
 * A MUI {@link Checkbox} that optionally supports a tri-state
 * checked / unchecked / indeterminate ("null") cycle, driven by
 * {@link MCheckboxProps.nullable} and {@link MCheckboxProps.defaultValue}.
 */
const MCheckbox: React.FC<MCheckboxProps> = (props) => {
    const { source, label, onChange,
        defaultValue = false,
        nullable = false, ...rest } = props;
    const defaultStatus: MCheckboxStatus[] = [
        { id: 0, checked: null, indeterminate: true },
        { id: 1, checked: true, indeterminate: false },
        { id: 2, checked: false, indeterminate: false }
    ];
    const statuses = defaultStatus.map((m) => Object.assign({}, m)); // clone array
    let allowNull = nullable;
    if (defaultValue === null || defaultValue === undefined) allowNull = true;
    if (!allowNull) statuses.shift(); // remove isnull;
    const defaultItem = statuses.find(x => x.checked === defaultValue) as MCheckboxStatus;

    const [item, setItem] = React.useState<MCheckboxStatus>(defaultItem);

    /**
     * First set default value, then change default value
     */
    function changeDefaultIfAny () {
        if (item.checked !== null && (defaultValue === null || defaultValue === undefined) ||
            item.checked === null && defaultValue !== null && defaultValue !== undefined
        ) {
            const next = statuses.find(x => x.checked === defaultValue);
            if (next) {
                setItem((prev) => ({ ...prev, ...next }));
                onChange(next.checked);
            }
        }
    }

    React.useEffect(() => {
        setItem((prev) => ({ ...prev, checked: defaultValue ?? null }));
        changeDefaultIfAny();
    }, [defaultValue]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        let next: MCheckboxStatus | undefined;
        if (allowNull) {
            let nextId = item.id + 1;
            if (nextId > 2) nextId = 0;
            next = statuses.find(x => x.id === nextId);
        } else {
            next = statuses.find(x => x.checked === checked);
        }
        if (!next) return;
        setItem((prev) => ({ ...prev, ...next }));
        onChange(next.checked);
    };

    return (
        <FormControlLabel
            control={
                <Checkbox {...rest}
                    name={source}
                    checked={item.checked ?? false}
                    indeterminate={item.indeterminate}
                    onChange={handleChange} />
            }
            label={label}
        />
    );
};

export default React.memo(MCheckbox);
